import { useTranslation } from "react-i18next";
import PageWrapper from "../PageWrapper/PageWrapper";
import { useState, useEffect, useRef } from "react";
import { useCommissionContractTemplate } from "../../hooks/useCommissionContractTemplate";
import PrimaryButton from "../../components/PrimaryButton/PrimaryButton";
import { TabV2, TabsList, TabsTrigger, TabsContent } from "../../components/TabV2/TabV2";
import CommissionContractAliasAPI from '../../api/CommissionContractAliasAPI';
import ComissionContractsAPI from '../../api/ComissionContractsAPI';
import { toastService } from "../../services/ToastService";
import GeneralSection from "./sections/GeneralSection";
import CommissionSettingsSection from "./sections/CommissionSettingsSection";
import FeesSection from "./sections/FeesSection";
import RequestFormSection from "./sections/RequestFormSection";
import TermsConditionsSection from "./sections/TermsConditionsSection";

export default function CommissionSettingsPage() {
  const { t } = useTranslation();
  const { contractTemplate, updateContractTemplate, updateContractTemplateLocal, locale } = useCommissionContractTemplate();
  const [contractLinkText, setContractLinkText] = useState("");
  const contractLink = `${window.location.origin}/drops/${contractLinkText || t("commission_settings_page.default_contract_link")}`;
  const [, setCommissionContractAlias] = useState("");
  const [draftTemplate, setDraftTemplate] = useState(contractTemplate);
  const [isUpdatingEnabled, setIsUpdatingEnabled] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const isUpdatingEnabledRef = useRef(false);

  useEffect(() => {
    CommissionContractAliasAPI.getAlias().then(res => {
      const alias = res.data || "";
      setCommissionContractAlias(alias);
      setContractLinkText(prev => prev || alias);
    });
  }, []);

  useEffect(() => {
    // Only update draft template if we're not currently updating the enabled status
    // This prevents the template reload from overwriting unsaved changes
    if (!isUpdatingEnabledRef.current) {
      setDraftTemplate(contractTemplate);
    }
  }, [contractTemplate]);

  const handleEnabledStatusChange = async (enabled: boolean) => {
    setIsUpdatingEnabled(true);
    isUpdatingEnabledRef.current = true;
    try {
      const response = await ComissionContractsAPI.updateEnabledStatus(locale, enabled);
      const updatedTemplate = response.data;
      setDraftTemplate(prev => ({ ...prev, enabled: updatedTemplate.enabled }));
      updateContractTemplateLocal({ enabled: updatedTemplate.enabled });
    } catch (error) {
      console.error('Error updating enabled status:', error);
      setDraftTemplate(prev => ({ ...prev, enabled: !enabled }));
    } finally {
      setIsUpdatingEnabled(false);
      setTimeout(() => {
        isUpdatingEnabledRef.current = false;
      }, 100);
    }
  };


  const handleSaveTemplate = async () => {
    setIsSaving(true);
    try {
      const fixedDraft = {
        ...draftTemplate,
        conditions: Array.isArray(draftTemplate.conditions)
          ? draftTemplate.conditions
          : [],
        formConditions: Array.isArray(draftTemplate.formConditions)
          ? draftTemplate.formConditions
          : [],
        enabled: draftTemplate.enabled,
      };
      
      await Promise.all([
        updateContractTemplate(fixedDraft),
        new Promise(resolve => setTimeout(resolve, 300))
      ]);
      
      toastService.showToast(
        t("commission_settings_page.toast.save_success") || "Commission contract settings saved successfully!",
        "success"
      );
    } catch (error) {
      console.error('Error saving commission contract settings:', error);
      toastService.showToast(
        t("commission_settings_page.toast.save_error") || "Failed to save commission contract settings. Please try again.",
        "error"
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <PageWrapper>
      <div className="flex flex-col gap-4">
        {/* General Section - Always visible */}
        <GeneralSection
          contractTemplate={draftTemplate}
          contractLink={contractLink}
          contractLinkText={contractLinkText}
          setContractLinkText={setContractLinkText}
          isUpdatingEnabled={isUpdatingEnabled}
          onEnabledStatusChange={handleEnabledStatusChange}
        />
        
        {/* Tabbed Sections */}
        <TabV2 defaultValue="commission-settings" className="w-full">
          <TabsList className="grid w-full grid-cols-4 py-[15px]">
            <TabsTrigger value="commission-settings">
              {t("commission_settings_page.tabs.commission_settings") || "Commission Settings"}
            </TabsTrigger>
            <TabsTrigger value="fees">
              {t("commission_settings_page.tabs.fees") || "Fees"}
            </TabsTrigger>
            <TabsTrigger value="request-form">
              {t("commission_settings_page.tabs.request_form") || "Request Form"}
            </TabsTrigger>
            <TabsTrigger value="terms-conditions">
              {t("commission_settings_page.tabs.terms_conditions") || "Terms & Conditions"}
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="commission-settings">
            <CommissionSettingsSection
              contractTemplate={draftTemplate}
              updateContractTemplate={update => setDraftTemplate(prev => ({ ...prev, ...update }))}
            />
          </TabsContent>
          
          <TabsContent value="fees">
            <FeesSection
              contractTemplate={draftTemplate}
              updateContractTemplate={update => setDraftTemplate(prev => ({ ...prev, ...update }))}
            />
          </TabsContent>
          
          <TabsContent value="request-form">
            <RequestFormSection
              contractTemplate={draftTemplate}
              updateContractTemplate={update => setDraftTemplate(prev => ({ ...prev, ...update }))}
            />
          </TabsContent>
          
          <TabsContent value="terms-conditions">
            <TermsConditionsSection
              contractTemplate={draftTemplate}
              updateContractTemplate={update => setDraftTemplate(prev => ({ ...prev, ...update }))}
            />
          </TabsContent>
        </TabV2>
        
        {/* Save Button */}
        <div className="flex justify-center pt-4">
          <PrimaryButton 
            text={t('commission_contract_template.save_button') || 'Save'}
            fn={handleSaveTemplate}
            isDisabled={isSaving}
            isLoading={isSaving}
          />
        </div>
      </div>
    </PageWrapper>
  );
}